import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { buildInitialDemoData } from '../demo/demoData';
import { createInsuranceTypeRecord, createPolicyRecord, createVendorRecord } from '../demo/demoFactories';
import { applyNotificationLedger, buildNotificationCandidates } from '../demo/demoNotifications';
import {
  createNotificationLedger,
  markAllNotificationsReadInLedger,
  markNotificationReadInLedger,
} from '../demo/demoNotificationLedger';
import { buildDemoStats, enrichPolicies } from '../demo/demoUtils';

const DemoContext = createContext();

export const useDemoContext = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemoContext must be used within DemoProvider');
  }
  return context;
};

export const DemoProvider = ({ children }) => {
  const initialData = useMemo(() => buildInitialDemoData(), []);
  const seedReadNotificationKeys = useMemo(
    () => new Set(initialData.readNotificationKeys || []),
    [initialData.readNotificationKeys]
  );

  const [vendors, setVendors] = useState(initialData.vendors);
  const [policyRecords, setPolicyRecords] = useState(initialData.policies);
  const [insuranceTypes, setInsuranceTypes] = useState(initialData.insuranceTypes);
  const nextVendorIdRef = useRef(100);
  const nextPolicyIdRef = useRef(100);
  const nextInsuranceTypeIdRef = useRef(100);
  const nextFieldIdRef = useRef(100);

  const policies = useMemo(
    () => enrichPolicies(policyRecords, vendors, insuranceTypes),
    [policyRecords, vendors, insuranceTypes]
  );

  const notificationCandidates = useMemo(
    () => buildNotificationCandidates(policies),
    [policies]
  );
  const [notificationLedger, setNotificationLedger] = useState(() => createNotificationLedger(seedReadNotificationKeys));

  const notifications = useMemo(
    () => applyNotificationLedger(notificationCandidates, notificationLedger, seedReadNotificationKeys),
    [notificationCandidates, notificationLedger, seedReadNotificationKeys]
  );

  const createVendor = useCallback((vendorData) => {
    const nextVendorId = nextVendorIdRef.current;
    nextVendorIdRef.current += 1;
    const newVendor = createVendorRecord(vendorData, { id: nextVendorId });
    setVendors((previousVendors) => [...previousVendors, newVendor]);
    return newVendor;
  }, []);

  const updateVendor = useCallback((vendorId, updates) => {
    setVendors((previousVendors) =>
      previousVendors.map((vendor) => (
        vendor.id === vendorId
          ? createVendorRecord({ ...vendor, ...updates, created_at: vendor.created_at }, { id: vendor.id })
          : vendor
      ))
    );
  }, []);

  const deleteVendor = useCallback((vendorId) => {
    setVendors((previousVendors) => previousVendors.filter((vendor) => vendor.id !== vendorId));
    setPolicyRecords((previousPolicies) => previousPolicies.filter((policy) => policy.vendor_id !== vendorId));
  }, []);

  const createPolicy = useCallback((policyData) => {
    const nextPolicyId = nextPolicyIdRef.current;
    nextPolicyIdRef.current += 1;
    const { rawPolicy, policy } = createPolicyRecord(policyData, {
      id: nextPolicyId,
      vendors,
      insuranceTypes,
    });

    setPolicyRecords((previousPolicies) => [...previousPolicies, rawPolicy]);
    return policy;
  }, [vendors, insuranceTypes]);

  const updatePolicy = useCallback((policyId, updates) => {
    setPolicyRecords((previousPolicies) =>
      previousPolicies.map((policy) => {
        if (policy.id !== policyId) {
          return policy;
        }

        const { rawPolicy } = createPolicyRecord(
          { ...policy, ...updates, created_at: policy.created_at },
          { id: policy.id, vendors, insuranceTypes }
        );
        return rawPolicy;
      })
    );
  }, [vendors, insuranceTypes]);

  const deletePolicy = useCallback((policyId) => {
    setPolicyRecords((previousPolicies) => previousPolicies.filter((policy) => policy.id !== policyId));
  }, []);

  const createInsuranceType = useCallback((typeData) => {
    const nextInsuranceTypeId = nextInsuranceTypeIdRef.current;
    const nextFieldId = nextFieldIdRef.current;
    const newType = createInsuranceTypeRecord(typeData, {
      id: nextInsuranceTypeId,
      nextFieldId,
    });

    setInsuranceTypes((previousTypes) => [...previousTypes, newType]);
    nextInsuranceTypeIdRef.current += 1;
    nextFieldIdRef.current += newType.custom_fields.length;
    return newType;
  }, []);

  const updateInsuranceType = useCallback((typeId, updates) => {
    setInsuranceTypes((previousTypes) =>
      previousTypes.map((insuranceType) => (
        insuranceType.id === typeId ? { ...insuranceType, ...updates } : insuranceType
      ))
    );
  }, []);

  const deleteInsuranceType = useCallback((typeId) => {
    setInsuranceTypes((previousTypes) => previousTypes.filter((insuranceType) => insuranceType.id !== typeId));
    setPolicyRecords((previousPolicies) =>
      previousPolicies.filter((policy) => policy.insurance_type_id !== typeId)
    );
  }, []);

  const markNotificationAsRead = useCallback((notificationId) => {
    setNotificationLedger((previousLedger) =>
      markNotificationReadInLedger(previousLedger, notificationId)
    );
  }, []);

  const markAllNotificationsAsRead = useCallback(() => {
    setNotificationLedger((previousLedger) =>
      markAllNotificationsReadInLedger(previousLedger, notificationCandidates)
    );
  }, [notificationCandidates]);

  const stats = useMemo(
    () => buildDemoStats(policies, vendors, notifications),
    [policies, vendors, notifications]
  );

  const value = {
    vendors,
    policies,
    insuranceTypes,
    notifications,
    stats,
    createVendor,
    updateVendor,
    deleteVendor,
    createPolicy,
    updatePolicy,
    deletePolicy,
    createInsuranceType,
    updateInsuranceType,
    deleteInsuranceType,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    isDemo: true,
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};
